<template>
	<app-resizable-dropdown
		ref="dropdownRef"
		position="bottom"
		trigger="click"
	>
		<template #activator>
			<button type="button" class="user-trigger" :title="profile?.Name ?? ''">
				<img
					v-if="profile?.PhotoLink"
					class="user-trigger__avatar user-trigger__avatar--img"
					:src="resolvePhotoUrl(profile.PhotoLink)"
					alt=""
				/>
				<span
					v-else
					class="user-trigger__avatar user-trigger__avatar--liter"
				>
					{{ avatarLiter }}
				</span>
			</button>
		</template>

		<div class="user-dd">
			<div
				class="user-dd__item user-dd__item--clickable"
				@click="onOpenSettings"
			>
				<app-icon
					name="user"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<div class="user-dd__name">{{ profile?.Name || '—' }}</div>
					<div class="user-dd__email">{{ profile?.Mail || '' }}</div>
				</div>
			</div>

			<div
				:class="[
					'user-dd__item',
					editingStatus ? 'user-dd__item--editing' : 'user-dd__item--clickable'
				]"
				@click="!editingStatus && onEditStatus()"
			>
				<app-icon
					name="comment"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<template v-if="!editingStatus">
						<span
							v-if="profile?.Status"
							class="user-dd__status-text"
						>
							{{ profile?.Status }}
						</span>
						<span
							v-else
							class="user-dd__status-placeholder"
						>
							Введите статус...
						</span>
					</template>
					<template v-else>
						<app-custom-textarea
							v-model="draftStatus"
							placeholder="Статус"
							:min-height="80"
							:max-height="400"
							auto-focus
							clear-hint-text="Очистить статус"
							@click.stop
						/>
						<div class="user-dd__status-actions">
							<app-button
								type="secondary"
								label="Отмена"
								@click="onCancelStatus"
							/>
							<app-button
								label="Сохранить"
								@click="onSaveStatus"
							/>
						</div>
					</template>
				</div>
			</div>

			<div
				class="user-dd__item user-dd__item--clickable"
				@click="onToggleSound"
			>
				<app-icon
					:name="soundIconName"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">{{ soundLabel }}</span>
				</div>
			</div>

			<div
				class="user-dd__item user-dd__item--clickable"
				@click="onLogout"
			>
				<app-icon
					name="logout"
					width="18px"
					color="#FFFFFF"
					class="user-dd__icon"
				/>
				<div class="user-dd__content">
					<span class="user-dd__text">Выйти из системы</span>
				</div>
			</div>
		</div>
	</app-resizable-dropdown>
</template>

<script setup lang="ts">
import {
	computed,
	ref,
	watch
} from "vue"
import { useRouter } from "vue-router"
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue"
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppButton from "@/components/ui-kit/button/AppButton.vue"
import { workerStore } from "@/store/worker"
import { headerStore } from "@/store/header"
import { clearSession } from "@/composables/useSession"
import { resolvePhotoUrl } from "@/utils/urlHelpers"

const router = useRouter()

const wStore = workerStore()
const hStore = headerStore()

const dropdownRef = ref<InstanceType<typeof AppResizableDropdown> | null>(null)

const profile = computed(() => wStore.workerProfile)

const onOpenSettings = () => {
	dropdownRef.value?.close()
	if (router.currentRoute.value.path !== '/settings') {
		router.push('/settings')
	}
}

const avatarLiter = computed(() => {
	const liter = profile.value?.PhotoLiter?.trim()
	if (liter) return liter
	const name = profile.value?.Name?.trim()
	return name ? name.charAt(0).toUpperCase() : '?'
})

const editingStatus = ref(false)
const draftStatus = ref<string>('')

watch(profile, (val) => {
	if (!editingStatus.value) {
		draftStatus.value = val?.Status ?? ''
	}
}, { immediate: true })

const onEditStatus = () => {
	draftStatus.value = profile.value?.Status ?? ''
	editingStatus.value = true
}

const onCancelStatus = () => {
	editingStatus.value = false
	draftStatus.value = profile.value?.Status ?? ''
}

const onSaveStatus = async () => {
	try {
		await wStore.setStatus(draftStatus.value)
		editingStatus.value = false
	} catch (e) {
		console.error('[HeaderUserDropdown] save status failed', e)
	}
}

const soundLabel = computed(() => hStore.enableSound ? 'Звук включен' : 'Звук выключен')

const soundIconName = computed(() => hStore.enableSound ? 'sound-on' : 'sound-off')

const onToggleSound = () => {
	hStore.toggleSound()
}

const onLogout = () => {
	clearSession()
	router.push('/login')
}
</script>

<style scoped>
.user-trigger {
	display: inline-flex;
	align-items: center;
	background: transparent;
	border: none;
	color: #fff;
	cursor: pointer;
	padding: 4px 0;
	font-family: inherit;
}

.user-trigger__avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 2px solid #FFFFFF;
	box-sizing: border-box;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.user-trigger__avatar--img {
	object-fit: cover;
}

.user-trigger__avatar--liter {
	background: #377444;
	color: #fff;
	font-size: 20px;
	font-weight: 600;
	line-height: 1;
}

.user-dd {
	display: flex;
	flex-direction: column;
	padding: 4px;
	background: #4D4D4D;
	color: #fff;
	min-width: 260px;
}

.user-dd__item {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 8px;
	border-radius: 4px;
	transition: background-color 0.1s;
}

.user-dd__item:hover {
	background-color: #5C5C5CE5;
}

.user-dd__item--clickable {
	cursor: pointer;
}

.user-dd__item--editing:hover {
	background-color: transparent;
}

.user-dd__icon {
	flex-shrink: 0;
}

.user-dd__content {
	display: flex;
	flex-direction: column;
	min-width: 0;
	width: 100%;
}

.user-dd__name {
	font-size: 14px;
	line-height: 18px;
	font-weight: 500;
}

.user-dd__email {
	font-size: 12px;
	line-height: 18px;
	font-weight: 400;
}

.user-dd__status-text,
.user-dd__text {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
}

.user-dd__status-placeholder {
	font-size: 14px;
	line-height: 18px;
	font-weight: 400;
	color: #D2D2D2;
}

.user-dd__status-actions {
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	margin-top: 4px;
}

.user-dd__content :deep(.app-custom-textarea) {
	background: #4D4D4D;
	color: #fff;
	border-color: rgba(255, 255, 255, 0.4);
}

.user-dd__content :deep(.app-custom-textarea::placeholder) {
	color: #D2D2D2;
}
</style>
